import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, SemanticUI } from '@iterable/deprecated_react_components';
import { GEO_LOCATION } from 'utils/fieldTypes';
import { DEFAULT_GEO_LOCATION } from 'utils/coordinates';
import { ButtonGroup } from 'ui/buttonGroup';
import { GroupButton } from 'ui/styledAsAngular';
import { selectUserMappingsByType } from 'modules/analytics/selectors';
import {
  changeQuerySizeLimit,
  changeQueryOrderBy,
  setUsesSmartRecos,
} from 'modules/queryBuilder/legacyQueryBuilder/actions';
import {
  selectAllFields,
  selectOrderByDynamicLocation,
  selectOrderByField,
  selectOrderByIsSortAsc,
  selectOrderByLocation,
  selectSizeLimit,
  selectSizeLimitMax,
  selectHasLocation,
  selectUsesSmartRecos,
} from 'modules/queryBuilder/legacyQueryBuilder/selectors';
import { RECOMMENDATIONS, ORDERED_BY_FIELD } from './constants';
import FieldDropdown from '../fieldDropdown';
import OrderByLocationPicker from '../limitAndOrderByRow/OrderByLocationPicker';
import {
  LimitAndOrderByRowContainer,
  InputContainer,
  StyledCoreDropdown,
  StyledNumberInput,
  StyledCoreDockingBar,
} from '../limitAndOrderByRow/LimitAndOrderByRow.styled';
import { FlexContainer } from './LimitAndRecosOrOrderByRow.styled';

// type Props = {
//   /** Additional content. */
//   content: node,
//   /** Function to call on toggle */
//   onToggle: () => {},
// };

const LimitAndRecosOrOrderByRow = ({ content, onToggle }) => {
  const dispatch = useDispatch();
  const dynamicLocation = useSelector(selectOrderByDynamicLocation);
  const field = useSelector(selectOrderByField);
  const fields = useSelector(selectAllFields);
  const isSortAsc = useSelector(selectOrderByIsSortAsc);
  const location = useSelector(selectOrderByLocation);
  const sizeLimit = useSelector(selectSizeLimit);
  const sizeLimitMax = useSelector(selectSizeLimitMax);
  const userMappings = useSelector(selectUserMappingsByType);
  const hasLocation = useSelector(selectHasLocation);
  const usesSmartRecos = useSelector(selectUsesSmartRecos);
  const [returnItemsBy, setReturnItemsBy] = useState();

  useEffect(() => {
    if (usesSmartRecos) {
      setReturnItemsBy(RECOMMENDATIONS);
    } else if (field) {
      setReturnItemsBy(ORDERED_BY_FIELD);
    }
  }, [usesSmartRecos, field]);

  /**
   * handleChangeField
   * Callback function when field is changed.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: The updated props containing the newly select field value and type.
   * @returns { void }
   */
  const handleChangeField = (e, { type, value }) => {
    const fieldIsGeo = type === GEO_LOCATION;
    const hasLocationAndIsGeo = hasLocation && fieldIsGeo;
    const newLocation = fieldIsGeo ? DEFAULT_GEO_LOCATION : undefined;

    const orderBy = {
      dynamicLocation: hasLocationAndIsGeo ? dynamicLocation : undefined,
      field: value,
      asc: isSortAsc,
      location: hasLocationAndIsGeo ? location : newLocation,
    };
    dispatch(changeQueryOrderBy(orderBy));
  };

  /**
   * handleChangeSizeLimit
   * Callback function when size limit is changed.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: The updated props containing the updated size limit value.
   * @returns { void }
   */
  const handleChangeSizeLimit = (e, { value }) => {
    let returnValue = value;
    if (sizeLimitMax && parseFloat(value, 10) > sizeLimitMax) {
      returnValue = `${sizeLimitMax}`;
    } else if (value === '0') {
      returnValue = '';
    }
    dispatch(changeQuerySizeLimit(returnValue));
  };

  /**
   * handleChangeSort
   * Callback function sort value if changed.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: The updated props with new sort value.
   * @returns { void }
   */
  const handleChangeSort = (e, { value }) => {
    const orderBy = {
      asc: value,
      dynamicLocation,
      field,
      location,
    };
    dispatch(changeQueryOrderBy(orderBy));
  };

  /**
   * handleChangeLocation
   * Callback function when location values are changed.
   * @param { Object } e: The change event.
   * @param { Object } locationValues: The updated location values.
   * @returns { void }
   */
  const handleChangeLocation = (e, locationValues) => {
    const orderBy = {
      asc: isSortAsc,
      field,
      ...locationValues,
    };
    dispatch(changeQueryOrderBy(orderBy));
  };

  /**
   * getSortOptions
   * Get the dropdown options for the sort by dropdown.
   * @returns { Array } The dropdown options.
   */
  const getSortOptions = () => [
    {
      text: 'Sort Ascending',
      icon: 'sort amount up',
      value: true,
    },
    {
      text: 'Sort Descending',
      icon: 'sort amount down',
      value: false,
    },
  ];

  const setUsesRecommendations = (isRecommendations /*: boolean */) => {
    setReturnItemsBy(isRecommendations ? RECOMMENDATIONS : ORDERED_BY_FIELD);
    onToggle();
    dispatch(setUsesSmartRecos(isRecommendations));
  };

  const getSizePlaceholder = () => (sizeLimitMax ? `Up to ${sizeLimitMax}` : '# of items');

  const hasLocationField = field && hasLocation;

  return (
    <StyledCoreDockingBar deferTime={200} dockOnBottom dockedClassName="cssLimitAndOrderDocked">
      <LimitAndOrderByRowContainer>
        <FlexContainer>
          <label>Items To Return</label>
          <InputContainer>
            <StyledNumberInput
              allowNegative={false}
              onChange={handleChangeSizeLimit}
              placeholder={getSizePlaceholder()}
              value={sizeLimit}
            />
          </InputContainer>
        </FlexContainer>
        <FlexContainer>
          <label>Ordered by</label>
          <ButtonGroup
            defaultSelectedId={
              returnItemsBy === RECOMMENDATIONS ? RECOMMENDATIONS : ORDERED_BY_FIELD
            }
            id="preview-view-options-button-group"
          >
            <GroupButton
              data-test="toggle-button-Recommendations"
              id={RECOMMENDATIONS}
              onSelect={() => setUsesRecommendations(true)}
            >
              RECOMMENDATIONS
              <Tooltip
                trigger={<SemanticUI.Icon name="question circle" />}
                position="bottom center"
                inverted
              >
                Utilize machine learning to <br />
                suggest related items.
              </Tooltip>
            </GroupButton>
            <GroupButton
              data-test="toggle-button-OrderedByField"
              id={ORDERED_BY_FIELD}
              onSelect={() => setUsesRecommendations(false)}
            >
              ORDER BY FIELD
            </GroupButton>
          </ButtonGroup>
        </FlexContainer>
        {returnItemsBy === ORDERED_BY_FIELD && (
          <>
            <FlexContainer>
              <label>Order by field</label>
              <InputContainer>
                <FieldDropdown
                  clearable
                  className="cssOrderByInputFieldDropdownInput"
                  fields={fields}
                  hideReadOnlyLabel
                  onChange={handleChangeField}
                  upward
                  value={field}
                  dataTest="orderByFieldDropdown"
                />
                <StyledCoreDropdown
                  disabled={!field}
                  floating
                  hideTextOnSelection
                  onChange={handleChangeSort}
                  options={getSortOptions()}
                  upward
                  value={isSortAsc}
                />
              </InputContainer>
            </FlexContainer>
            {hasLocationField && (
              <FlexContainer>
                <label>From Location</label>
                <InputContainer>
                  <OrderByLocationPicker
                    dynamicLocation={dynamicLocation}
                    location={location}
                    onChange={handleChangeLocation}
                    userMappings={userMappings}
                  />
                </InputContainer>
              </FlexContainer>
            )}
          </>
        )}
        {content}
      </LimitAndOrderByRowContainer>
    </StyledCoreDockingBar>
  );
};

export default LimitAndRecosOrOrderByRow;
